import { useGetMyUserQuery, useProjectId, useServiceName } from '@consigli/hooks';
import { ProjectRole, ServiceName } from '@consigli/types';
import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Breadcrumb } from '@/features/breadcrumb/breadcrumb';
import { ViewerProvider } from '@/features/document-viewer/use-viewer-context';
import { Route } from '@/routes';
import { serviceConfig } from '@/util/service-config';

export const ServiceDetailWrapper: FC = () => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const serviceName = useServiceName();
  const navigate = useNavigate();
  const { data: user } = useGetMyUserQuery();

  const hasAccess = useMemo(() => {
    if (!user) return true;
    if (user.isSuperuser || user.isOrgAdmin) return true;

    const projectAccess = user.projectAccess.find((p) => p.id === projectId);
    if (!projectAccess) return false;
    if (projectAccess.role === ProjectRole.ADMIN) return true;

    const serviceEntry = serviceConfig.find((s) => s.serviceType === serviceName);
    if (!serviceEntry) {
      console.warn(
        `ServiceDetailWrapper: no serviceConfig entry for "${serviceName}" — denying access`,
      );
      return false;
    }
    const { servicePermissionName } = serviceEntry;
    if (!servicePermissionName) return true;
    return projectAccess.services.includes(servicePermissionName);
  }, [user, projectId, serviceName]);

  useEffect(() => {
    if (user && !hasAccess) {
      toast.error('You do not have access to this service.');
      navigate(`/${Route.PROJECTS}`, { replace: true });
    }
  }, [user, hasAccess, navigate]);

  return (
    <ViewerProvider>
      <Breadcrumb
        label={t(`services.${serviceName}`)}
        to={`/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}`}
        asLink={serviceName !== ServiceName.MEETING_SECRETARY}
      />
      {user === undefined ? null : hasAccess ? <Outlet /> : null}
    </ViewerProvider>
  );
};
